import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Shield, CheckCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, API } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, provide a generic error
        throw new Error("Unable to connect to server. Please try again.");
      }

      if (!response.ok) {
        // Provide user-friendly error messages
        if (response.status === 401) {
          throw new Error("Invalid email or password. Please try again.");
        } else if (response.status === 422) {
          throw new Error("Please enter a valid email address.");
        } else {
          throw new Error(data.detail || "Login failed. Please try again.");
        }
      }

      login(data.user, data.token);
      toast.success("Welcome back!");
      
      if (data.user.role === "admin" || data.user.role === "consultant" || data.user.role === "super_admin") {
        navigate("/admin");
      } else if (data.user.role === "referral_partner") {
        navigate("/partner");
      } else if (data.user.role === "business_dev") {
        navigate("/sales");
      } else if (data.user.role === "sdr") {
        navigate("/sdr");
      } else {
        navigate("/portal");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex animate-fade-in">
      <Helmet>
        <title>Client Login - RNB Consulting Group</title>
        <meta name="description" content="Log in to your RNB Consulting client portal. Access your projects, files, invoices, time tracking, and team communication." />
      </Helmet>
      {/* Left Side - Brand Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero" />
        <img
          src="https://images.unsplash.com/photo-1758691737387-a89bb8adf768?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Modern healthcare team"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
              <span className="text-white font-heading font-bold text-2xl">R</span>
            </div>
            <span className="font-heading font-semibold text-xl">RNB Consulting</span>
          </Link>
          
          <div className="space-y-8">
            <div>
              <h2 className="font-heading text-4xl font-bold mb-4 leading-tight">
                Transform Your Healthcare Practice
              </h2>
              <p className="text-lg text-white/80 max-w-md">
                Access your client portal to manage projects, track hours, and collaborate with your consultant.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-rnb-teal" />
                </div>
                <span className="text-white/90">Real-time project tracking</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-rnb-teal" />
                </div>
                <span className="text-white/90">Secure document sharing</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-rnb-teal" />
                </div>
                <span className="text-white/90">Direct consultant messaging</span>
              </div>
            </div>
          </div>

          <p className="text-sm text-white/60">
            Trusted by healthcare organizations nationwide
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-subtle">
        <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-rnb-blue/5 rounded-full blur-3xl translate-y-1/2" />
        
        <div className="relative z-10 w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="flex lg:hidden items-center justify-center space-x-2 mb-8">
            <div className="w-12 h-12 bg-rnb-blue rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold text-2xl">R</span>
            </div>
            <span className="font-heading font-semibold text-2xl text-rnb-navy">RNB Consulting</span>
          </Link>

          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-rnb-navy mb-2">Welcome Back</h1>
            <p className="text-rnb-gray">Sign in to access your client portal</p>
          </div>

          <div className="card-modern p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-rnb-navy font-medium">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-rnb-gray" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 h-12 border-gray-200 focus:border-rnb-teal focus:ring-rnb-teal/20"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-rnb-navy font-medium">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-rnb-blue hover:text-rnb-teal transition-colors font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-rnb-gray" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-11 h-12 border-gray-200 focus:border-rnb-teal focus:ring-rnb-teal/20"
                    required
                    data-testid="login-password"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  data-testid="remember-me-checkbox"
                />
                <Label
                  htmlFor="remember-me"
                  className="text-sm text-rnb-gray cursor-pointer select-none"
                >
                  Remember me for 30 days
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full h-12 btn-primary text-base font-semibold"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Sign In
                    <ArrowRight className="h-5 w-5" />
                  </span>
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-rnb-gray mt-6">
              Need an account?{" "}
              <span className="text-rnb-navy font-medium">Contact your administrator</span>
            </p>
            
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-center text-sm text-green-800">
                <span className="font-medium">Referral Partner?</span>{" "}
                <Link to="/partner/login" className="text-green-700 hover:underline font-semibold">
                  Log in to your Partner Dashboard →
                </Link>
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mt-6 text-sm text-rnb-gray">
            <Shield className="h-4 w-4" />
            <span>Secured with 256-bit encryption</span>
          </div>

          <p className="text-center text-xs text-rnb-gray mt-4">
            By signing in, you agree to our{" "}
            <Link to="/terms" className="text-rnb-blue hover:underline">Terms</Link>
            {" "}and{" "}
            <Link to="/privacy" className="text-rnb-blue hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
